import React, { useEffect } from 'react';
import { GetStaticProps } from 'next';
import configureSentryScope from '../../helpers/configureSentryScope';
import getArticleCategoryPageViewModels from '../../helpers/getArticleCategoryPageViewModels';
import { GameViewModel } from '../../../resources/js/typings/viewModels/gameViewModel';
import useViewModels, {
  ViewModels,
  ViewModelsProvider,
} from '../../contexts/useViewModels';
import { GameContextProvider } from '../../../resources/js/contexts/useGame';
import { SeriesOfArticlesProvider } from '../../contexts/useSeriesOfArticles';
import ArticleCategoryViewModel from '../../../resources/js/typings/viewModels/articleCategoryViewModel';
import PageOfArticlePreviewsViewModel from '../../../resources/js/typings/viewModels/pageOfArticlePreviewsViewModel';
import ArticleCategory from '../../components/ArticleCategory/ArticleCategory';
import buildComponentViewModelsFromEnhancedMarkdowns from '../../helpers/buildComponentViewModelsFromEnhancedMarkdowns';
import CmsNavigationViewModel from '../../../resources/js/typings/viewModels/cmsNavigationViewModel';
import ArchonLayout from '../../layouts/ArchonLayout/ArchonLayout';
import {
  ArchonHeaderViewModel,
  getGameLabel,
} from '../../components/ArchonHeader/ArchonHeader';
import { ArchonFooterViewModel } from '../../components/ArchonFooter/ArchonFooter';
import { initRequest } from '../../helpers/rpgLogsApiRequestMetadata';

type Props = {
  pageNumber: number;
  game: GameViewModel;
  header: ArchonHeaderViewModel;
  footer: ArchonFooterViewModel;
  articleCategory: ArticleCategoryViewModel;
  navigation: CmsNavigationViewModel;
  pageOfArticlePreviews: PageOfArticlePreviewsViewModel;
  viewModels: ViewModels;
};

export default function ArticleCategoryPage({
  pageNumber,
  game,
  header,
  footer,
  articleCategory,
  navigation,
  pageOfArticlePreviews,
  viewModels,
}: Props): JSX.Element {
  const InnerArticleCategoryPage = ({
    viewModels,
  }: {
    viewModels: ViewModels;
  }) => {
    const { setViewModels } = useViewModels();
    useEffect(() => {
      setViewModels(viewModels);
    }, [viewModels, setViewModels]);

    return (
      <ArchonLayout
        header={header}
        footer={footer}
        breadcrumbs={pageOfArticlePreviews.breadcrumbs}
      >
        <ArticleCategory
          pageNumber={pageNumber}
          articleCategory={articleCategory}
          navigation={navigation}
          pageOfArticlePreviews={pageOfArticlePreviews}
          gameLabel={getGameLabel(header)}
        />
      </ArchonLayout>
    );
  };

  return (
    <ViewModelsProvider value={viewModels}>
      <GameContextProvider game={game}>
        <SeriesOfArticlesProvider>
          <InnerArticleCategoryPage viewModels={viewModels} />
        </SeriesOfArticlesProvider>
      </GameContextProvider>
    </ViewModelsProvider>
  );
}

export const getCategoryPageStaticProps: (
  // Needs to be passed as argument instead of import to prevent node libraries attempting to be imported on client side
  getPlaiceholder: (src: string) => Promise<unknown>
) => GetStaticProps =
  (getPlaiceholder) =>
  async ({ params }) => {
    params ??= {};
    params.pageNumber = params.pageNumber ?? '1';
    configureSentryScope(params);

    if (
      !params?.articleCategorySlug ||
      typeof params.articleCategorySlug !== 'string' ||
      !params?.pageNumber ||
      typeof params.pageNumber !== 'string' ||
      !params?.gameSlug ||
      typeof params.gameSlug !== 'string' ||
      !isPositiveInteger(params.pageNumber)
    )
      return {
        notFound: true,
      };

    if (params.gameSlug === 'classic-wrath') {
      return {
        redirect: {
          statusCode: 307,
          destination: `/classic-cata/articles/${params.articleCategorySlug}${
            params.pageNumber === '1' ? '' : `/page/${params.pageNumber}`
          }`,
        },
      };
    }

    initRequest({
      route:
        params.pageNumber === '1'
          ? '/[gameSlug]/articles/[articleCategorySlug]'
          : '/[gameSlug]/articles/[articleCategorySlug]/page/[pageNumber]',
      path: `/${params.gameSlug}/articles/${params.articleCategorySlug}${
        params.pageNumber === '1' ? '' : `/page/${params.pageNumber}`
      }`,
    });

    const pageNumber = Number(params.pageNumber);

    const pageViewModels = await getArticleCategoryPageViewModels(
      params.articleCategorySlug,
      pageNumber,
      params.gameSlug
    );

    if (
      !pageViewModels.articleCategory ||
      pageViewModels.pageOfArticlePreviews.articlePreviews.length === 0
    )
      return {
        notFound: true,
      };

    const context = {
      pageViewModels,
      getPlaiceholder,
    };

    const viewModels = await buildComponentViewModelsFromEnhancedMarkdowns(
      pageViewModels.pageOfArticlePreviews.articlePreviews.map(
        (articlePreview) => `<Image src="${articlePreview.imageUrl}">`
      ),
      context
    );

    return {
      props: {
        pageNumber,
        googleAnalytics: pageViewModels.googleAnalytics,
        game: pageViewModels.game,
        header: pageViewModels.header,
        footer: pageViewModels.footer,
        articleCategory: pageViewModels.articleCategory,
        navigation: pageViewModels.cmsNavigation,
        pageOfArticlePreviews: pageViewModels.pageOfArticlePreviews,
        viewModels,
      },
      revalidate: 600,
    };
  };

function isPositiveInteger(value: string) {
  return /^\d+$/.test(value);
}
